import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Tag} from "primereact/tag";

import PageLayout from "../PageLayout";
import Input from "../../ui/Input";
import Btn from "../../ui/Btn";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";
import "../../common/game.css";

const GameTeam = ({role}) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const headerTemplate = () => (
    <div className="flex align-items-center gap-2 font-semibold">
      <i className="pi pi-users text-primary" aria-hidden="true"/>
      <span>{appStore.tr.club_members}</span>
    </div>
  );

  const itemTemplate = (member) => (
    <div className={`flex align-items-center gap-3 w-full p-2 border-round-lg ${member.selected ? "surface-ground" : ""}`}>
      <Button
        type="button"
        icon={member.selected ? "pi pi-check" : "pi pi-plus"}
        severity={member.selected ? "success" : "secondary"}
        outlined={!member.selected}
        rounded
        size="small"
        onClick={() => {
          if (member.selected) clubStore.tournament.deleteMemberFromTeam(clubStore.tournament.gameTeam.id, member.id);
          else clubStore.tournament.addMemberToTeam(clubStore.tournament.gameTeam.id, member.id);
        }}
      />
      <div className="flex-1 min-w-0 flex flex-wrap align-items-center gap-2">
        {member.year_born && <Tag value={member.year_born}/>}
        <span className="font-medium overflow-wrap-anywhere">{member.title}</span>
      </div>
      <span className="badge badge-tournament flex-shrink-0">{member.participated}/{member.total}</span>
    </div>
  );

  return (
    <PageLayout
      header={
        <div className="flex align-items-center gap-3 w-full">
          <span className="competition-form__page-icon"><i className="pi pi-users" aria-hidden="true"/></span>
          <h3 className="m-0">{appStore.tr.game_team}</h3>
        </div>
      }
      filters={
        <div className="col-12">
          <div className="competition-form__section">
            <div className="competition-form__section-heading">
              <span className="competition-form__section-icon"><i className="pi pi-pencil" aria-hidden="true"/></span>
              <h4>{appStore.tr.name}</h4>
            </div>
            <div className="competition-form__field">
              <Input type="text" store={clubStore.tournament.gameTeam} prop="name" label={appStore.tr.name} id="name"/>
            </div>
            <div className="flex justify-content-end">
              <Btn icon="pi pi-save" label={appStore.tr.save} severity="success" className="w-full md:w-auto"
                disabled={!clubStore.tournament.gameTeam.name}
                onClick={() => {
                  clubStore.tournament.saveGameTeam().then((ret) => {
                    systemStore.setSuccess(appStore.tr.successfully_saved);
                    navigate(`/${role === "MGR" ? "manager" : "trainer"}/games/game_team/${ret.game_team.id}`);
                  }).catch((error) => {});
                }}/>
            </div>
          </div>
        </div>
      }
    >
      {clubStore.tournament.gameTeam.id > 0 && (
        <div className="col-12">
          <div className="competition-form__section">
            <div className="flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="competition-form__section-heading">
                <span className="competition-form__section-icon"><i className="pi pi-user-plus" aria-hidden="true"/></span>
                <h4>{appStore.tr.team_members}</h4>
                <span className="badge badge-tournament">{clubStore.tournament.gameTeam.members.length}</span>
              </div>
              <Button icon="pi pi-users" size="small" severity="secondary" label={appStore.tr.club_members}
                onClick={() => {
                  setVisible(true);
                  clubStore.tournament.getMembersForGameTeam(clubStore.tournament.gameTeam.id);
                }}/>
            </div>

            {clubStore.tournament.gameTeam.members.length > 0 ? (
              <div className="grid m-0">
                {clubStore.tournament.gameTeam.members.map((member) => (
                  <div key={member.id} className="col-12 md:col-6 p-2">
                    <div className="competition-detail-item flex align-items-center gap-2 h-full">
                      <i className="pi pi-user text-primary" aria-hidden="true"/>
                      <span className="flex-1 min-w-0 font-medium">{member.title}</span>
                      {member.year_born && <Tag value={member.year_born} severity="primary"/>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="surface-ground border-round-lg p-4 text-center text-color-secondary">{appStore.tr.not_found}</div>
            )}
          </div>

          <Dialog header={appStore.tr.game_team} visible={visible} className="w-11 md:w-9 lg:w-7"
            onHide={() => {
              if (!visible) return;
              setVisible(false);
            }}>
            <div className="flex flex-column gap-3">
              <div className="flex flex-wrap align-items-end gap-2 surface-ground border-round-lg p-3">
                <div className="flex-grow-1">
                  <Input type="text" store={clubStore.tournament.membersQuery} prop="title"
                    label={appStore.tr.title} className="w-full"/>
                </div>
                <div className="flex-grow-1">
                  <Input type="text" store={clubStore.tournament.membersQuery} prop="year_born"
                    label={appStore.tr.year_born} className="w-full"/>
                </div>
                <Btn icon="pi pi-search" label={appStore.tr.search}
                  onClick={() => clubStore.tournament.getMembersForGameTeam(clubStore.tournament.gameTeam.id)}/>
              </div>
              <DataTable value={clubStore.tournament.members} emptyMessage={appStore.tr.not_found}
                className="w-full" size="small">
                <Column field="title" header={headerTemplate} body={itemTemplate}/>
              </DataTable>
            </div>
          </Dialog>
        </div>
      )}
    </PageLayout>
  );
};

export default observer(GameTeam);
